import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Plus, Pencil, Trash2, ToggleLeft, ToggleRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialState = {
  full_name: '',
  languages: 'Indonesian, Arabic',
  years_of_experience: 0,
  specialization: 'umrah',
  certification: '',
  rating: 5.0,
  price_per_group: 0,
};

const MuthowifsManagement = () => {
  const [muthowifs, setMuthowifs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(initialState);
  const [saving, setSaving] = useState(false);

  const fetchMuthowifs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/master/muthowifs?show_all=true`);
      setMuthowifs(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching muthowifs:', error);
      toast.error('Gagal memuat data muthowif');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMuthowifs(); }, [fetchMuthowifs]);

  const handleOpen = (muthowif = null) => {
    if (muthowif) {
      setEditing(muthowif);
      setFormData({
        full_name: muthowif.full_name || '',
        languages: Array.isArray(muthowif.languages) ? muthowif.languages.join(', ') : 'Indonesian, Arabic',
        years_of_experience: muthowif.years_of_experience || 0,
        specialization: muthowif.specialization || 'umrah',
        certification: muthowif.certification || '',
        rating: muthowif.rating || 5.0,
        price_per_group: muthowif.price_per_group || 0,
      });
    } else {
      setEditing(null);
      setFormData(initialState);
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        full_name: formData.full_name,
        languages: formData.languages.split(',').map(l => l.trim()).filter(Boolean),
        years_of_experience: parseInt(formData.years_of_experience),
        specialization: formData.specialization,
        certification: formData.certification,
        rating: parseFloat(formData.rating),
        price_per_group: parseFloat(formData.price_per_group),
      };
      if (editing) {
        await axios.put(`${API}/master/muthowifs/${editing.id}`, payload);
        toast.success('Muthowif berhasil diupdate');
      } else {
        await axios.post(`${API}/master/muthowifs`, payload);
        toast.success('Muthowif berhasil ditambahkan');
      }
      setShowModal(false);
      fetchMuthowifs();
    } catch (err) {
      toast.error(editing ? 'Gagal update muthowif' : 'Gagal menambah muthowif');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await axios.put(`${API}/master/muthowifs/${id}`, { is_active: !currentStatus });
      toast.success('Status berhasil diubah');
      fetchMuthowifs();
    } catch {
      toast.error('Gagal mengubah status');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Yakin ingin menghapus muthowif ini?')) return;
    try {
      await axios.delete(`${API}/master/muthowifs/${id}`);
      toast.success('Muthowif berhasil dihapus');
      fetchMuthowifs();
    } catch {
      toast.error('Gagal menghapus muthowif');
    }
  };

  if (loading) return <div className="text-center py-8">Memuat data...</div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold font-['Outfit']">Muthowif Management</h1>
        <Button className="rounded-full" onClick={() => handleOpen()}>
          <Plus className="h-4 w-4 mr-2" /> Tambah Muthowif
        </Button>
      </div>

      <Card className="rounded-2xl">
        <CardHeader><CardTitle>Daftar Muthowif</CardTitle></CardHeader>
        <CardContent>
          {muthowifs.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">Belum ada data muthowif</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left p-4 font-semibold">Nama</th>
                    <th className="text-left p-4 font-semibold">Bahasa</th>
                    <th className="text-left p-4 font-semibold">Pengalaman</th>
                    <th className="text-left p-4 font-semibold">Spesialisasi</th>
                    <th className="text-left p-4 font-semibold">Rating</th>
                    <th className="text-left p-4 font-semibold">Harga/Grup</th>
                    <th className="text-left p-4 font-semibold">Status</th>
                    <th className="text-right p-4 font-semibold">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {muthowifs.map((m) => (
                    <tr key={m.id} className="border-b hover:bg-muted/50">
                      <td className="p-4 font-medium">{m.full_name}</td>
                      <td className="p-4 text-muted-foreground">{Array.isArray(m.languages) ? m.languages.join(', ') : m.languages}</td>
                      <td className="p-4">{m.years_of_experience} thn</td>
                      <td className="p-4"><Badge variant="secondary">{m.specialization}</Badge></td>
                      <td className="p-4">{m.rating} ⭐</td>
                      <td className="p-4">Rp {m.price_per_group?.toLocaleString('id-ID')}</td>
                      <td className="p-4">
                        <Badge variant={m.is_active ? 'default' : 'secondary'}>
                          {m.is_active ? 'Aktif' : 'Nonaktif'}
                        </Badge>
                      </td>
                      <td className="p-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => handleToggleActive(m.id, m.is_active)}>
                            {m.is_active ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => handleOpen(m)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDelete(m.id)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Muthowif' : 'Tambah Muthowif Baru'}</DialogTitle>
            <DialogDescription>Lengkapi form untuk {editing ? 'mengupdate' : 'menambahkan'} muthowif</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="full_name">Nama Lengkap *</Label>
                <Input id="full_name" value={formData.full_name} onChange={(e) => setFormData({...formData, full_name: e.target.value})} required />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="specialization">Spesialisasi *</Label>
                  <select className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={formData.specialization} onChange={(e) => setFormData({...formData, specialization: e.target.value})} required>
                    <option value="umrah">Umrah</option>
                    <option value="haji">Haji</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="certification">Sertifikasi *</Label>
                  <Input id="certification" value={formData.certification} onChange={(e) => setFormData({...formData, certification: e.target.value})} placeholder="MUI, Kemenag" required />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="languages">Bahasa</Label>
                  <Input id="languages" value={formData.languages} onChange={(e) => setFormData({...formData, languages: e.target.value})} placeholder="Indonesian, Arabic" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="years_of_experience">Pengalaman (thn) *</Label>
                  <Input id="years_of_experience" type="number" min="0" value={formData.years_of_experience} onChange={(e) => setFormData({...formData, years_of_experience: e.target.value})} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rating">Rating</Label>
                  <Input id="rating" type="number" min="0" max="5" step="0.1" value={formData.rating} onChange={(e) => setFormData({...formData, rating: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="price_per_group">Harga/Grup *</Label>
                <Input id="price_per_group" type="number" min="0" value={formData.price_per_group} onChange={(e) => setFormData({...formData, price_per_group: e.target.value})} required />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Batal</Button>
              <Button type="submit" disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editing ? 'Update' : 'Tambah'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MuthowifsManagement;
